// =============================================================================
// Portal Payment Methods Page — read-only list + Stripe Billing Portal (Model B)
// -----------------------------------------------------------------------------
// Stripe's hosted Billing Portal is the single management surface for cards
// (add / remove / set default / SCA / expiry updates) across BOTH the Portal
// and the embedded Shopify app. Our PCI surface stays at zero — card data never
// touches our servers and we build no card-entry UI.
//
// This page only RENDERS the cards (a read-only projection reconciled from
// Stripe on read by the API) and hands off all mutations to the Billing Portal
// via "Manage payment methods". On return, the list re-reconciles automatically.
// =============================================================================

import { useState } from 'react';
import { Link } from 'react-router';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useApiClient } from '@tkpod/api-client';
import type { PaymentMethodDto } from '@tkpod/shared';
import { Button, Card, CardHeader, Spinner } from '@tkpod/ui';

export function PortalPaymentMethodsPage() {
  const api = useApiClient();
  const [banner, setBanner] = useState<
    { tone: 'success' | 'error' | 'info'; message: string } | null
  >(null);

  const listQuery = useQuery<PaymentMethodDto[]>({
    queryKey: ['payment-methods'],
    queryFn: () => api.get<PaymentMethodDto[]>('/api/v1/payment-methods'),
  });

  // Open the Stripe-hosted Billing Portal. Stripe handles add / remove /
  // set-default; on close the merchant lands back here and the list re-reads.
  const portalMutation = useMutation({
    mutationFn: () =>
      api.post<{ url: string }>('/api/v1/payment-methods/billing-portal-session', {
        returnUrl: `${window.location.origin}/billing?tab=payment-methods`,
      }),
    onSuccess: (data) => {
      window.location.href = data.url;
    },
    onError: (err) =>
      setBanner({
        tone: 'error',
        message:
          err instanceof Error
            ? err.message
            : 'Could not open the payment-methods portal — please retry shortly.',
      }),
  });

  const items = listQuery.data ?? [];
  const defaultMethod = items.find((m) => m.isDefault) ?? null;
  const otherMethods = items.filter((m) => !m.isDefault);

  return (
    <div className="space-y-6">
      {banner && (
        <div
          role="status"
          className={
            banner.tone === 'success'
              ? 'rounded-md border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-900 dark:border-emerald-800 dark:bg-emerald-950 dark:text-emerald-100'
              : banner.tone === 'error'
                ? 'rounded-md border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-900 dark:border-rose-800 dark:bg-rose-950 dark:text-rose-100'
                : 'rounded-md border border-sky-200 bg-sky-50 px-4 py-3 text-sm text-sky-900 dark:border-sky-800 dark:bg-sky-950 dark:text-sky-100'
          }
        >
          <div className="flex items-start justify-between gap-3">
            <span>{banner.message}</span>
            <button
              type="button"
              onClick={() => setBanner(null)}
              className="text-xs font-medium underline opacity-80 hover:opacity-100"
            >
              Dismiss
            </button>
          </div>
        </div>
      )}

      <Card>
        <CardHeader title="Payment methods" />
        <div className="space-y-4 px-4 pb-5 sm:px-5">
          <div className="flex items-start justify-between gap-3">
            <p className="max-w-xl text-sm text-text-secondary">
              Cards saved here are charged when you{' '}
              <Link
                to="/billing?tab=wallet"
                className="font-medium text-brand-600 hover:underline dark:text-brand-400"
              >
                top up your wallet
              </Link>{' '}
              and when auto-recharge fires. Adding, removing, and changing your
              default card is handled by Stripe's secure Billing Portal — card
              numbers never touch Transfer Kingdom.
            </p>
            <Button
              onClick={() => portalMutation.mutate()}
              disabled={portalMutation.isPending}
            >
              {portalMutation.isPending ? 'Opening…' : 'Manage payment methods'}
            </Button>
          </div>

          {listQuery.isLoading ? (
            <div className="flex h-32 items-center justify-center">
              <Spinner />
            </div>
          ) : listQuery.isError ? (
            <div className="space-y-2">
              <p className="text-sm text-rose-600">
                {listQuery.error instanceof Error
                  ? listQuery.error.message
                  : 'Unable to load payment methods.'}
              </p>
              <Button variant="secondary" onClick={() => listQuery.refetch()}>
                Retry
              </Button>
            </div>
          ) : items.length === 0 ? (
            <div className="rounded-md border border-dashed border-border px-6 py-10 text-center">
              <p className="text-sm font-medium text-text-primary">
                No saved payment methods yet
              </p>
              <p className="mt-1 text-xs text-text-secondary">
                Use “Manage payment methods” to add a card via Stripe and enable
                wallet top-ups and auto-recharge.
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {defaultMethod && (
                <PaymentMethodRow key={defaultMethod.id} method={defaultMethod} />
              )}
              {otherMethods.map((m) => (
                <PaymentMethodRow key={m.id} method={m} />
              ))}
            </div>
          )}
        </div>
      </Card>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Read-only row — display only; all mutations go through the Billing Portal.
// ---------------------------------------------------------------------------

function PaymentMethodRow({ method }: { method: PaymentMethodDto }) {
  const card = method.card;
  const bank = method.bank;

  return (
    <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-border px-4 py-3">
      <div className="flex items-start gap-3">
        <div className="rounded-md border border-border bg-surface-tertiary p-2 text-xs font-medium uppercase tracking-wide text-text-secondary">
          {method.type === 'CARD'
            ? 'Card'
            : method.type === 'BANK_ACCOUNT'
              ? 'Bank'
              : 'Wallet'}
        </div>
        <div>
          {method.type === 'CARD' && card && (
            <p className="text-sm font-medium text-text-primary">
              {card.brand?.toUpperCase() ?? 'Card'} •••• {card.last4 ?? '••••'}
              {card.expMonth && card.expYear && (
                <span className="ml-2 text-xs font-normal text-text-secondary">
                  Exp {String(card.expMonth).padStart(2, '0')}/
                  {String(card.expYear).slice(-2)}
                </span>
              )}
            </p>
          )}
          {method.type === 'BANK_ACCOUNT' && bank && (
            <p className="text-sm font-medium text-text-primary">
              {bank.bankName ?? 'Bank account'} •••• {bank.last4 ?? '••••'}
            </p>
          )}
          {method.type === 'WALLET_ONLY' && (
            <p className="text-sm font-medium text-text-primary">
              Wallet-only (no payment method)
            </p>
          )}
          <p className="mt-0.5 text-xs text-text-tertiary">
            Added {new Date(method.createdAt).toLocaleDateString()}
            {method.isDefault && (
              <span className="ml-2 inline-flex items-center rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-emerald-700 dark:bg-emerald-900 dark:text-emerald-200">
                Default
              </span>
            )}
          </p>
        </div>
      </div>
    </div>
  );
}
